import { useState, useEffect, useCallback } from 'react';
import quickbooksService, { QuickBooksApiError } from '../services/quickbooksService';
import { useAccessToken } from './useAuth';

/**
 * Custom hook for QuickBooks OAuth integration
 * Handles connection status, OAuth flow, and token management
 * 
 * Backend now stores connection info in database:
 * - Realm ID (QuickBooks company ID)
 * - Access token & refresh token
 * - Token expiry dates (access: 1hr, refresh: 100 days)
 * 
 * @returns {Object} QuickBooks state and methods
 */
export const useQuickBooks = () => {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { getBackendAccessToken } = useAccessToken();

    /**
     * Check QuickBooks connection status
     * Fetches from backend database
     */
    const checkStatus = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const token = await getBackendAccessToken();
            const statusData = await quickbooksService.getStatus(token);
            setStatus(statusData);
        } catch (err) {
            const errorMessage = err instanceof QuickBooksApiError 
                ? err.message 
                : 'Failed to check QuickBooks status';
            setError(errorMessage);
            console.error('Error checking QuickBooks status:', err);
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken]);

    /**
     * Initiate QuickBooks OAuth flow
     * Opens popup window for authorization
     */
    const connect = useCallback(async () => {
        try {
            setError(null);
            const token = await getBackendAccessToken();
            const authResponse = await quickbooksService.getAuthUrl(token);
            
            // Open QuickBooks auth in popup window
            const width = 600;
            const height = 700;
            const left = window.screenX + (window.outerWidth - width) / 2;
            const top = window.screenY + (window.outerHeight - height) / 2;
            
            const popup = window.open(
                authResponse.authorizationUrl,
                'QuickBooks Authorization',
                `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no`
            );
            
            // Listen for success/error messages from popup
            // Backend returns HTML that posts messages back to parent window
            const handleMessage = (event) => {
                if (event.data.type === 'QUICKBOOKS_AUTH_SUCCESS') {
                    console.log('QuickBooks connected successfully!', event.data.realmId);
                    popup?.close();
                    checkStatus(); // Refresh status from database
                    window.removeEventListener('message', handleMessage);
                } else if (event.data.type === 'QUICKBOOKS_AUTH_ERROR') {
                    const errorMsg = event.data.error || 'Failed to connect to QuickBooks';
                    
                    // Ignore malformed backend error messages (template syntax errors)
                    if (errorMsg.includes('escapeJs(e.getMessage())') || errorMsg.includes('" + ')) {
                        console.warn('Ignoring malformed error message from backend, checking actual status...');
                        popup?.close();
                        window.removeEventListener('message', handleMessage);
                        // Check actual status to see if connection succeeded despite error message
                        checkStatus();
                        return;
                    }
                    
                    console.error('QuickBooks connection failed:', errorMsg);
                    setError(errorMsg);
                    popup?.close();
                    window.removeEventListener('message', handleMessage);
                }
            };
            
            window.addEventListener('message', handleMessage);
            
            // Cleanup listener if popup is closed manually
            const checkPopupClosed = setInterval(() => {
                if (popup?.closed) {
                    clearInterval(checkPopupClosed);
                    window.removeEventListener('message', handleMessage);
                    // Refresh status in case connection succeeded
                    checkStatus();
                }
            }, 1000);
            
        } catch (err) {
            const errorMessage = err instanceof QuickBooksApiError 
                ? err.message 
                : 'Failed to connect to QuickBooks';
            setError(errorMessage);
            console.error('Error connecting to QuickBooks:', err);
        }
    }, [getBackendAccessToken, checkStatus]);

    /**
     * Disconnect from QuickBooks
     * Revokes tokens and deletes connection from database
     */
    const disconnect = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const token = await getBackendAccessToken();
            await quickbooksService.disconnect(token);
            
            // Refresh status after disconnect
            await checkStatus();
        } catch (err) {
            const errorMessage = err instanceof QuickBooksApiError 
                ? err.message 
                : 'Failed to disconnect from QuickBooks';
            setError(errorMessage);
            console.error('Error disconnecting from QuickBooks:', err);
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, checkStatus]);

    /**
     * Manually refresh access token (usually auto-refreshed by backend)
     */
    const refreshToken = useCallback(async () => {
        try {
            setError(null);
            const token = await getBackendAccessToken();
            await quickbooksService.refreshToken(token);
            await checkStatus();
        } catch (err) {
            const errorMessage = err instanceof QuickBooksApiError 
                ? err.message 
                : 'Failed to refresh token';
            setError(errorMessage);
            console.error('Error refreshing QuickBooks token:', err);
        }
    }, [getBackendAccessToken, checkStatus]);

    // Check status on mount
    useEffect(() => {
        checkStatus();
    }, [checkStatus]);

    return {
        status,
        loading,
        error,
        isConnected: status?.authenticated ?? false,
        connect,
        disconnect,
        refreshToken,
        refetch: checkStatus
    };
};

export default useQuickBooks;
